import { AnprVM } from 'shell/view-models/search-anpr';
import { AxiosResponse } from 'axios';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData } from 'shell/models/person';
import { DateTime } from 'luxon';
import { CertificateFormVM } from 'front-office/view-models/certificate/certificate-form';
import { transformToChoiceDate } from 'shell/components/shared/choice-date/functions';
import { DocumentRadioCertificate } from 'front-office/constants/document-radio-certificate';

export const transformerPersonGeneralityList = (
  response: AxiosResponse<HttpResponse<ResponseAnprData>>
): AnprVM<CertificateFormVM> => {
  return {
    warnings: (response.data.payload?.risposta3002OK?.listaAnomalie?.errore || []).map(
      (warning) => ({
        id: warning.codiceErroreAnomalia,
        value: warning.testoErroreAnomalia,
      })
    ),
    errors: (response.data.payload?.rispostaKO?.listaErrori?.errore || []).map((error) => ({
      id: error.codiceErroreAnomalia,
      value: error.testoErroreAnomalia,
    })),
    persons: (response.data.payload?.risposta3002OK?.listaSoggetti?.datiSoggetto || []).map(
      (person) => {
        const birthChoiceDate = transformToChoiceDate(
          person.generalita?.senzaGiorno,
          person.generalita?.senzaGiornoMese
        );

        const citizenship = person.cittadinanza || [];

        const isBritish = !!citizenship.find((citizen) => citizen.codiceStato === '219');

        const residenceArray = person.residenza || [];

        let birthplace = '';
        // birthplace check for object
        if (person.generalita?.luogoNascita?.localita) {
          birthplace = `${person.generalita?.luogoNascita?.localita?.descrizioneLocalita} (${person.generalita?.luogoNascita?.localita?.descrizioneStato})`;
        }

        if (person.generalita?.luogoNascita?.comune) {
          birthplace = `${person.generalita?.luogoNascita?.comune?.nomeComune || ''} (${
            person.generalita?.luogoNascita?.comune?.siglaProvinciaIstat
          })`;
        }
        // Residence check for object
        let residence = '';
        // scelgo il primo o è un array?
        if (residenceArray[0]?.indirizzo) {
          residence = `${residenceArray[0]?.indirizzo.comune?.nomeComune || ''} ${
            residenceArray[0]?.indirizzo.comune?.siglaProvinciaIstat
              ? `(${residenceArray[0]?.indirizzo.comune?.siglaProvinciaIstat || ''})`
              : ''
          }`;
        }
        // scelgo il primo o è un array?
        if (residenceArray[0]?.localitaEstera) {
          residence = `${
            residenceArray[0]?.localitaEstera.indirizzoEstero?.localita?.descrizioneLocalita || ''
          } ${
            residenceArray[0]?.localitaEstera.indirizzoEstero?.localita?.descrizioneStato
              ? `(${residenceArray[0]?.localitaEstera.indirizzoEstero.localita.descrizioneStato})`
              : ''
          }`;
        }

        let state = '';
        // state check for object
        if (residenceArray[0]?.indirizzo?.toponimo) {
          const toponym = residenceArray[0]?.indirizzo?.toponimo;

          const civic = residenceArray[0]?.indirizzo?.numeroCivico;

          state = `${toponym?.specie || ''} ${toponym?.denominazioneToponimo || ''},${
            civic?.numero || ''
          }${civic?.esponente1 ? `/${civic.esponente1}${civic.lettera || ''}` : ''}`;
        }

        if (person.generalita?.soggettoAIRE && person.generalita?.soggettoAIRE === 'S') {
          state = 'Iscritto AIRE';
        }

        if (person.datiCancellazione && person.datiCancellazione?.descrizioneMotivoCancellazione) {
          state = `${person.datiCancellazione.descrizioneMotivoCancellazione}`;
        }

        let cartaIdentitaComuneRilascio = '';

        if (!!person.cartaIdentita?.consolatoRilascio) {
          cartaIdentitaComuneRilascio =
            person.cartaIdentita?.consolatoRilascio?.descrizioneConsolato || '';
        } else if (person.cartaIdentita?.comuneRilascio) {
          cartaIdentitaComuneRilascio =
            `Comune di ${person.cartaIdentita?.comuneRilascio?.nomeComune}` || '';
        }

        const isItalian = !!citizenship.find((citizen) => citizen.codiceStato === '100');

        let address = '';

        if (residenceArray[0]?.indirizzo?.toponimo) {
          const toponym = residenceArray[0]?.indirizzo?.toponimo;

          const civic = residenceArray[0]?.indirizzo?.numeroCivico;

          address = `${toponym?.specie || ''} ${toponym?.denominazioneToponimo || ''},${
            civic?.numero || ''
          }${civic?.esponente1 ? `/${civic.esponente1}${civic.lettera || ''}` : ''}`;
        } else if (residenceArray[0]?.localitaEstera?.indirizzoEstero) {
          const toponym = residenceArray[0]?.localitaEstera?.indirizzoEstero?.toponimo;

          const civic = residenceArray[0]?.localitaEstera?.indirizzoEstero?.toponimo?.numeroCivico;

          address = `${toponym?.denominazione || ''} ${civic || ''}`;
        }

        return {
          isAire: person.generalita?.soggettoAIRE === 'S',
          isBritish,
          anomaly:
            response.data?.payload?.risposta3002OK?.noteAnomalie?.map((anomalia) => ({
              key: anomalia?.key,
              code: anomalia?.codiceAnomalia,
              description: anomalia?.descrizioneNota,
              flag: anomalia?.flagBlocco,
              insertDate: anomalia?.key?.dataAnnotazione
                ? DateTime.fromISO(anomalia.key?.dataAnnotazione).toFormat('dd/MM/yyyy')
                : undefined,
              cancelDate: anomalia?.dataCancellazione
                ? DateTime.fromISO(anomalia.dataCancellazione).toFormat('dd/MM/yyyy')
                : undefined,
            })) || [],
          certifiableApplicant: person.attributiSoggetto?.soggettoCertificabile !== '0',
          certifiableAccountholder: person.attributiSoggetto?.soggettoCertificabile !== '0',

          applicant: {
            isItalian,
            fiscalCode: person.generalita?.codiceFiscale?.codFiscale || '',
            surname: person.generalita?.cognome || '',
            name: person.generalita?.nome || '',
            gender: person.generalita?.sesso || '',
            withoutName: person.generalita?.senzaNome === '1' ? true : false,
            withoutSurname: person.generalita?.senzaCognome === '1' ? true : false,
            birthDate: person.generalita?.dataNascita
              ? DateTime.fromISO(person.generalita?.dataNascita)
              : null,
            birthChoiceDate,
          },
          generality: {
            dati3002: person,
            witness1: '',
            witness1IdentityCard: '',
            witness2: '',
            witness2IdentityCard: '',
            isItalian,
            birthDate: person.generalita?.dataNascita
              ? DateTime.fromISO(person.generalita?.dataNascita)
              : null,
            birthChoiceDate,
            isForeign: person.generalita?.luogoNascita?.localita ? true : false,
            foreignStateBirth: person.generalita?.luogoNascita?.localita
              ? {
                  id: person.generalita?.luogoNascita?.localita?.codiceStato || '',
                  value: person.generalita?.luogoNascita?.localita?.descrizioneStato || '',
                }
              : null,
            birthplace,
            state,
            residenceString: residence,
            addressString: address,
            provinceOfBirth: person.generalita?.luogoNascita?.localita?.provinciaContea || '',
            foreignMunicipality: person.generalita?.luogoNascita?.localita
              ? {
                  id: person.generalita?.luogoNascita?.localita?.codiceStato || '',
                  description: person.generalita?.luogoNascita?.localita?.descrizioneLocalita || '',
                  province: person.generalita?.luogoNascita?.localita?.provinciaContea || '',
                }
              : null,
            // provinceOfBirth: person.generalita?.luogoNascita?.comune?.siglaProvinciaIstat || '',
            municipalityOfBirth: person.generalita?.luogoNascita?.comune
              ? {
                  id: person.generalita?.luogoNascita?.comune?.codiceIstat || '',
                  description: person.generalita?.luogoNascita?.comune?.nomeComune || '',
                  province: person.generalita?.luogoNascita?.comune?.siglaProvinciaIstat || '',
                }
              : null,
            locality: person.generalita?.luogoNascita?.comune?.descrizioneLocalita,
            numberDocument: (person.cartaIdentita?.numero || '').replace(`Carta d'identità`, ''),
            dateReleaseDocument: person.cartaIdentita?.dataRilascio
              ? DateTime.fromISO(person.cartaIdentita?.dataRilascio)
              : null,
            isForeignDocument: person.cartaIdentita?.consolatoRilascio
              ? DocumentRadioCertificate.Other
              : DocumentRadioCertificate.IdentityCard,
            consulateCode: person.cartaIdentita?.consolatoRilascio?.descrizioneConsolato,
            municipalityDocument: person.cartaIdentita?.comuneRilascio
              ? {
                  id: person.cartaIdentita?.comuneRilascio?.codiceIstat || '',
                  description: person.cartaIdentita?.comuneRilascio?.nomeComune || '',
                  province: person.cartaIdentita?.comuneRilascio?.siglaProvinciaIstat || '',
                }
              : null,
            localityDocument: person.cartaIdentita?.comuneRilascio?.descrizioneLocalita || '',
            // documento luogo rilascio
            locationDocument: person.cartaIdentita?.comuneRilascio?.nomeComune,
            // residenza
            residence: (person.residenza || [])[0],
            // idSchedaSoggettoAnpr
            idSubjectAnpr: person.generalita?.idSchedaSoggettoANPR,
            // codiceStato
            idState: citizenship[0]?.codiceStato,
            // idFamigliaConvivenzaAnpr
            idFamilyAnpr: person.famigliaConvivenza?.idFamigliaConvivenzaANPR,
            documentString: `Documento di riconoscimento ${
              person.cartaIdentita?.numero || ''
            } rilasciato il ${
              person.cartaIdentita?.dataRilascio
                ? DateTime.fromISO(person.cartaIdentita?.dataRilascio).toFormat('dd/MM/yyyy')
                : ''
            } da ${cartaIdentitaComuneRilascio}`,
          },
          hidden: {
            delete: true,
            edit: true,
          },
        };
      }
    ) as Array<CertificateFormVM>,
  };
};
